import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageContainer from "../components/pageContainer/PageContainer";
import PageActionsNavbar from "../components/navbars/PageActionsNavbar";
import StyledInput from "../components/styledInput/StyledInput";
import Pagination from "../components/pagination/Pagination";
import Button from "../components/button";
import ModalCreateWorker from "../components/modal/dataModal/data/modalCreateWorker/ModalCreateWorker";
import useModal from "../components/modal/useModal";
import UseContext from "../hooks/useContext";
import { Plus, SearchLoop, SendIcon, Synchronisation, Pencil } from "../assets/icons";
import useTitle from "../hooks/TitleManager";
import useIntegrations from "../hooks/useIntegrations";
import ApiDatabase from "../server";
import { EmployeesSidebarGhost } from "../components/ghost/SettingsAndEmployeesGhost";

const PAGE_SIZE = 20;

const SOURCE_LABEL = {
  workfully: "Workfully",
  peopulse: "Peopulse",
  jobbiz: "Jobbiz",
};

const BASE_SOURCE_FILTERS = ["workfully"];

const formatSourceLabel = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  const key = raw.toLowerCase();
  if (SOURCE_LABEL[key]) return SOURCE_LABEL[key];
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

// One badge per (source, id) built from bigCompanyExternalIds.
const matriculesFromWorker = (worker) => {
  if (!worker) return [];
  const list = [];
  const seen = new Set();

  const pushUnique = (sourceLabel, id) => {
    const idStr = id != null ? String(id).trim() : "";
    if (!idStr || !sourceLabel) return;
    const key = `${sourceLabel.toLowerCase()}:${idStr}`;
    if (seen.has(key)) return;
    seen.add(key);
    list.push({ source: sourceLabel, id: idStr });
  };

  for (const link of worker.bigCompanyExternalIds || []) {
    const externalId = link.externalId || link.peopulseId;
    if (!externalId) continue;
    const rawSource = link.source || (link.peopulseId ? "peopulse" : null);
    if (!rawSource) continue;
    pushUnique(formatSourceLabel(rawSource), externalId);
  }

  return list;
};

const sourcesFromWorker = (worker) => {
  const sources = new Set();

  for (const link of worker?.bigCompanyExternalIds || []) {
    const source = link.source || (link.peopulseId ? "peopulse" : null);
    if (source) sources.add(String(source).toLowerCase());
  }

  if (sources.size === 0) sources.add("workfully");
  return sources;
};

const workerMatchesSourceFilter = (worker, source) => {
  const workerSources = sourcesFromWorker(worker);

  if (source === "workfully") {
    return workerSources.size === 1 && workerSources.has("workfully");
  }

  return workerSources.has(source);
};

const Employees = () => {
  useTitle({ title: "Employé" });
  const integrations = useIntegrations();

  const [workers, setWorkers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceSelected, setSourceSelected] = useState([]);
  const [missingPhoneFilter, setMissingPhoneFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const requestIdRef = useRef(0);
  const { toggle } = useModal();
  const { setModalData } = UseContext();

  const fetchWorkers = useCallback(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoading(true);
    ApiDatabase.getWorkers(
      { search, page, limit: PAGE_SIZE },
      (data) => {
        if (requestId !== requestIdRef.current) return;
        const list = data?.workers || [];
        setWorkers(list);
        setTotal(data?.total || 0);
        setTotalPages(data?.totalPages || 1);
        setSelected((prev) => {
          if (list.length === 0) return null;
          const match = prev && list.find((w) => String(w._id) === String(prev._id));
          return match || list[0];
        });
        setIsLoading(false);
      },
      () => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      }
    );
  }, [search, page]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [sourceSelected, missingPhoneFilter]);

  const sourceFilters = useMemo(() => {
    const sources = [...BASE_SOURCE_FILTERS];
    if (integrations.peopulse) sources.push("peopulse");
    if (integrations.jobbiz) sources.push("jobbiz");
    return sources;
  }, [integrations.peopulse, integrations.jobbiz]);

  const sourceFilterList = useMemo(
    () =>
      sourceFilters.map((source) => ({
        value: source,
        label: SOURCE_LABEL[source],
        checked: sourceSelected.includes(source),
      })),
    [sourceFilters, sourceSelected],
  );

  const sourceFilterValue =
    sourceSelected.length > 0
      ? sourceSelected.map((source) => SOURCE_LABEL[source]).join(", ")
      : "Source";

  const handleChangeSources = (updatedSources) => {
    setSourceSelected(
      updatedSources
        .filter((source) => source.checked)
        .map((source) => source.value),
    );
  };

  const displayedWorkers = useMemo(() => {
    let filtered = workers;

    if (sourceSelected.length > 0) {
      filtered = filtered.filter((worker) =>
        sourceSelected.some((source) => workerMatchesSourceFilter(worker, source))
      );
    }

    if (missingPhoneFilter) {
      filtered = filtered.filter((worker) => !worker.phone);
    }

    return filtered;
  }, [sourceSelected, missingPhoneFilter, workers]);

  const selectedId = selected?._id;

  useEffect(() => {
    if (isLoading) return;
    if (displayedWorkers.length === 0) {
      setSelected(null);
      return;
    }
    const selectedStillVisible = displayedWorkers.some(
      (worker) => String(worker._id) === String(selectedId),
    );
    if (!selectedStillVisible) setSelected(displayedWorkers[0]);
  }, [displayedWorkers, isLoading, selectedId]);

  const handleOpenEditPhone = () => {
    if (!selected) return;
    setModalData({
      idWorker: selected._id,
      phone: selected.phone || "",
      firstname: selected.firstname,
      lastname: selected.lastname,
    });
    toggle("edit_worker_phone");
  };

  const handleOpenRegeneratePin = () => {
    if (!selected) return;
    setModalData({
      idWorker: selected._id,
    });
    toggle("edit_worker_token");
  };

  const handleOpenSendPin = () => {
    if (!selected) return;
    setModalData({
      worker: selected,
    });
    toggle("send_pin_sms");
  };

  const matriculesOfSelected = useMemo(
    () => (selected ? matriculesFromWorker(selected) : []),
    [selected]
  );

  const workerName = (worker) => {
    if (!worker) return "—";
    return `${worker.firstname || ""} ${worker.lastname || ""}`.trim() || "—";
  };

  const hasPinCode = Boolean(selected?.pinCode && String(selected.pinCode).trim());

  return (
    <PageContainer type="employees">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 px-[20px] pb-4 pt-3">
          <PageActionsNavbar
            actions={
              <Button color="PRIMARY" size="SM" onClick={() => setCreateOpen(true)}>
                <div className="flex items-center gap-1">
                  <Plus wh={18} color="#FFFFFF" />
                  <span>Ajouter un employé</span>
                </div>
              </Button>
            }
          >
            <div className="w-[280px] max-w-full shrink-0 text-sm">
              <StyledInput
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                imgFront={<SearchLoop wh={18} color="#9CA3AF" />}
              />
            </div>
            <div className="w-[150px] max-w-full shrink-0 text-sm">
              <StyledInput
                type="selectMultipleCheckbox"
                value={sourceFilterValue}
                list={sourceFilterList}
                onChange={handleChangeSources}
              />
            </div>
            <div className="w-[180px] max-w-full shrink-0 text-sm">
              <StyledInput
                type="selectMultipleCheckbox"
                value={missingPhoneFilter ? "Numéro manquant" : "Numéro"}
                list={[
                  {
                    value: "missing",
                    label: "Numéro manquant",
                    checked: missingPhoneFilter,
                  },
                ]}
                onChange={(updated) => {
                  const item = updated.find((u) => u.value === "missing");
                  setMissingPhoneFilter(item?.checked || false);
                }}
              />
            </div>
          </PageActionsNavbar>
        </div>

        <div className="h-px shrink-0 bg-gray-100" aria-hidden="true" />

        {/* Content - Split Layout */}
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden px-[20px] pt-2 pb-6">
          {/* Left Sidebar - Worker List */}
          <div className="w-[240px] flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <EmployeesSidebarGhost />
              ) : displayedWorkers.length === 0 ? (
                <div className="p-4 text-gray-500 text-sm">
                  {workers.length === 0
                    ? "Aucun employé."
                    : "Aucun employé pour ce filtre."}
                </div>
              ) : (
                displayedWorkers.map((worker) => (
                  <div
                    key={worker._id}
                    onClick={() => setSelected(worker)}
                    className={`w-full text-left p-3 rounded cursor-pointer ${
                      selected && String(selected._id) === String(worker._id)
                        ? "bg-gray-100"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {worker.firstname} {worker.lastname}
                    </div>
                    <div className="text-xs text-gray-500 truncate min-h-[16px]">
                      {worker.phone || ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Content - Worker Detail */}
          <div className="flex-1 flex flex-col border rounded-lg bg-white p-6 overflow-y-auto">
            {!selected ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                Sélectionnez un employé
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {/* Header - Name and Matricules */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    {workerName(selected)}
                  </h2>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {matriculesOfSelected.length === 0 ? (
                      <span className="text-sm text-gray-500">—</span>
                    ) : (
                      matriculesOfSelected.map((m) => (
                        <span
                          key={`${m.source}-${m.id}`}
                          className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                        >
                          {m.source} : {m.id}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                {/* Info Cards */}
                <div className="flex gap-4 text-sm">
                  {/* Phone Card */}
                  <div className="flex-1 border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Téléphone</div>
                        <div className="text-gray-700">{selected.phone || "—"}</div>
                      </div>
                      <Button
                        color="ICON"
                        size="ICON"
                        onClick={handleOpenEditPhone}
                        title="Modifier le numéro"
                      >
                        <Pencil wh={18} color="#374151" />
                      </Button>
                    </div>
                  </div>

                  {/* PIN Card with Actions */}
                  <div className="flex-[2] border rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-gray-900 mb-1">Code PIN</div>
                        <div
                          className={
                            hasPinCode ? "text-gray-700" : "text-gray-400 italic"
                          }
                        >
                          {selected.pinCode || "Aucun code pour le moment"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          color={hasPinCode ? "ICON" : "DISABLED"}
                          size="ICON"
                          disabled={!hasPinCode}
                          onClick={() => hasPinCode && handleOpenSendPin()}
                          title={
                            hasPinCode
                              ? "Envoyer le PIN par SMS"
                              : "Aucun code PIN à envoyer"
                          }
                        >
                          <SendIcon
                            wh={18}
                            color={hasPinCode ? "#374151" : "#6b7280"}
                          />
                        </Button>
                        <Button
                          color={hasPinCode ? "SECONDARY" : "DISABLED"}
                          size="SM"
                          disabled={!hasPinCode}
                          onClick={() =>
                            hasPinCode && handleOpenRegeneratePin()
                          }
                          title={
                            hasPinCode
                              ? "Générer un nouveau code PIN"
                              : "Aucun code PIN à régénérer"
                          }
                        >
                          <div className="flex items-center gap-1">
                            <Synchronisation
                              wh={16}
                              color={hasPinCode ? "#374151" : "#6b7280"}
                            />
                            <span>Générer un nouveau</span>
                          </div>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Pagination */}
        <div className="flex justify-end py-3 border-t border-gray-200">
          <Pagination
            currentPage={page}
            countPerPage={PAGE_SIZE}
            countTotal={total}
            totalPages={totalPages}
            setCurrentPage={setPage}
          />
        </div>
      </div>

      {/* Create Worker Modal */}
      <ModalCreateWorker
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          setPage(1);
          fetchWorkers();
        }}
      />
    </PageContainer>
  );
};

export default Employees;
